import { ReactNode, useState } from "react";

import * as Dialog from "@radix-ui/react-dialog";

import { backdrop } from "../../styles/backdrop.css";
import { hasHardwareCompositing } from "../../utils/hardwareCompositing";

import { AiHeader } from "./AiHeader";
import * as styles from "./Drawer.css";
import { DrawerFooter } from "./DrawerFooter";
import { DrawerHeader } from "./DrawerHeader";

interface AiDrawerProps {
    variant: "ai";
    title: string;
    infoTooltipText: string;
    betaLabel?: string;
}

interface DefaultDrawerProps {
    variant: "default";
    withHeader?: { title: string };
    withFooter?: ReactNode;
}

type DrawerProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
    preventIndirectClose?: boolean;
    closeAriaLabel?: string;
    onOpenAutoFocus?: Dialog.DialogContentProps["onOpenAutoFocus"];
} & (AiDrawerProps | DefaultDrawerProps);

export function Drawer(props: DrawerProps) {
    const {
        open,
        onOpenChange,
        children,
        variant,
        preventIndirectClose,
        closeAriaLabel,
        onOpenAutoFocus,
    } = props;
    const [isExpanded, setExpanded] = useState(false);

    function onBeforeClose(e: Event) {
        if (preventIndirectClose) {
            e.preventDefault();
        }
    }

    const close = () => onOpenChange(false);

    const { withHeader, withFooter } = props.variant === "default" ? props : {};

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay
                    className={backdrop}
                    data-blur={hasHardwareCompositing()}
                />
                <Dialog.Content
                    className={styles.dialogContentVariant[variant]}
                    data-expanded={isExpanded}
                    onInteractOutside={onBeforeClose}
                    onEscapeKeyDown={onBeforeClose}
                    onOpenAutoFocus={onOpenAutoFocus}
                >
                    {variant === "ai" && (
                        <AiHeader
                            title={props.title}
                            infoTooltipText={props.infoTooltipText}
                            expanded={isExpanded}
                            onExpandedChange={setExpanded}
                            onClose={close}
                            closeAriaLabel={closeAriaLabel}
                            betaLabel={props.betaLabel}
                        />
                    )}
                    {withHeader && (
                        <DrawerHeader
                            title={withHeader.title}
                            onClose={close}
                            closeAriaLabel={closeAriaLabel}
                        />
                    )}
                    {withHeader || withFooter ? (
                        <div className={styles.dialogBody}>{children}</div>
                    ) : (
                        children
                    )}
                    {withFooter && <DrawerFooter>{withFooter}</DrawerFooter>}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
Drawer.Title = Dialog.Title;
